// Coordinated Substation Attack — Autonomy Trace scenario pack
// 7-event campaign: recon → relay trip → cold restart → MMS tamper → GOOSE spoof → SSH pivot → containment

const PROTO = {
  DNP3: 'dnp3',
  MMS: 'iec61850_mms',
  GOOSE: 'iec61850_goose',
  SSH: 'ssh',
  TRUST: 'trust',
  MONITOR: 'monitoring',
};

const STATE = {
  NORMAL: 'normal',
  SUSPICIOUS: 'suspicious',
  CRITICAL: 'critical',
  ISOLATED: 'isolated',
  AI: 'ai',
};

// ---------------------------------------------------------------------------
// Initial topology — authorized assets only, no rogue device
// ---------------------------------------------------------------------------

export const INITIAL_NODES = [
  {
    id: 'ai-engine',
    type: 'device',
    position: { x: 420, y: 20 },
    data: {
      label: 'AI Sentry',
      role: 'AI Engine',
      ip: 'cloud',
      state: STATE.AI,
      alertCount: 0,
      policyHits: 0,
      icon: '⚡',
    },
  },
  {
    id: 'gateway',
    type: 'device',
    position: { x: 420, y: 160 },
    data: {
      label: 'Substation Gateway',
      role: 'Gateway',
      ip: '192.168.1.65',
      state: STATE.NORMAL,
      alertCount: 0,
      policyHits: 0,
      icon: '⬡',
    },
  },
  {
    id: 'scada',
    type: 'device',
    position: { x: 140, y: 310 },
    data: {
      label: 'SCADA Master',
      role: 'SCADA Master',
      ip: '192.168.1.68',
      state: STATE.NORMAL,
      alertCount: 0,
      policyHits: 0,
      icon: '◈',
    },
  },
  {
    id: 'rtu',
    type: 'device',
    position: { x: 420, y: 310 },
    data: {
      label: 'RTU',
      role: 'RTU',
      ip: '192.168.1.70',
      sublabel: 'Mitsubishi FX5U',
      state: STATE.NORMAL,
      alertCount: 0,
      policyHits: 0,
      icon: '▣',
    },
  },
  {
    id: 'relay',
    type: 'device',
    position: { x: 700, y: 310 },
    data: {
      label: 'Protection Relay',
      role: 'Relay',
      ip: '192.168.1.71',
      sublabel: 'SEL-351',
      state: STATE.NORMAL,
      alertCount: 0,
      policyHits: 0,
      icon: '⚙',
    },
  },
  {
    id: 'ied',
    type: 'device',
    position: { x: 700, y: 470 },
    data: {
      label: 'IED',
      role: 'IED',
      ip: '192.168.1.72',
      sublabel: 'Beckhoff CX5130',
      state: STATE.NORMAL,
      alertCount: 0,
      policyHits: 0,
      icon: '⬡',
    },
  },
];

export const INITIAL_EDGES = [
  {
    id: 'ai-gateway',
    source: 'ai-engine',
    target: 'gateway',
    data: { protocol: PROTO.MONITOR, state: 'normal', label: 'monitoring' },
  },
  {
    id: 'scada-gateway',
    source: 'scada',
    target: 'gateway',
    data: { protocol: PROTO.TRUST, state: 'normal', label: '' },
  },
  {
    id: 'gateway-rtu',
    source: 'gateway',
    target: 'rtu',
    data: { protocol: PROTO.DNP3, state: 'normal', label: 'DNP3' },
  },
  {
    id: 'gateway-relay',
    source: 'gateway',
    target: 'relay',
    data: { protocol: PROTO.MMS, state: 'normal', label: 'MMS' },
  },
  {
    id: 'gateway-ied',
    source: 'gateway',
    target: 'ied',
    data: { protocol: PROTO.MMS, state: 'normal', label: 'MMS' },
  },
  {
    id: 'rtu-relay-goose',
    source: 'rtu',
    target: 'relay',
    data: { protocol: PROTO.GOOSE, state: 'normal', label: 'GOOSE' },
  },
];

// ---------------------------------------------------------------------------
// Campaign events
// ---------------------------------------------------------------------------

export const CAMPAIGN = {
  id: 'substation-coordinated-attack-v1',
  title: 'Coordinated Substation Attack',
  description: 'Multi-protocol attack against substation protection assets: rogue SCADA master performing recon, relay trip via DNP3, cold restart attempt, MMS tampering, GOOSE spoofing, and SSH pivot to IED.',
  environment: 'Substation Alpha — 115kV Transmission',

  events: [
    // -----------------------------------------------------------------------
    // EVT 0 — T+00s — Rogue polling (P37, P39 — Warning)
    // -----------------------------------------------------------------------
    {
      id: 'evt-000',
      t_offset_ms: 0,
      timestamp: '15:38:00',
      title: 'Rogue DNP3 polling detected',
      protocol: PROTO.DNP3,
      source_ip: '192.168.1.100',
      source_mac: null,
      source_role: 'Unknown',
      dest_ip: '192.168.1.70',
      dest_role: 'RTU',
      device_name: 'RTU — Mitsubishi FX5U',
      policy_ids: ['P37', 'P39'],
      severity: 'Warning',
      action_status: 'alerted',
      card: {
        observed: 'Unknown host 192.168.1.100 sent 14 DNP3 FC=1 (Read) frames to RTU 192.168.1.70 over 90 seconds',
        inferred: 'Unauthorized SCADA master enumerating RTU data object inventory — mapping available control points before write commands',
        why_it_matters: 'Baseline enumeration is stage 1 of most DNP3 attacks. Adversary is profiling register layout and response timing.',
        recommendation: 'Monitor 192.168.1.100. Do not block yet — capture full session. Flag for containment if write commands follow.',
        confidence: '87%',
      },
      topology_delta: {
        nodes_add: [
          {
            id: 'rogue',
            type: 'device',
            position: { x: 120, y: 470 },
            data: {
              label: 'Rogue Device',
              role: 'Unknown',
              ip: '192.168.1.100',
              sublabel: 'Unauthorized',
              state: STATE.SUSPICIOUS,
              alertCount: 2,
              policyHits: 2,
              icon: '?',
            },
          },
        ],
        edges_add: [
          {
            id: 'rogue-rtu-dnp3',
            source: 'rogue',
            target: 'rtu',
            data: { protocol: PROTO.DNP3, state: 'suspicious', label: 'DNP3 FC=1' },
          },
        ],
        nodes_update: {
          rtu: { alertCount: 2, policyHits: 2 },
        },
      },
    },

    // -----------------------------------------------------------------------
    // EVT 1 — T+30s — Direct Operate to relay (P35, P38 — Warning)
    // -----------------------------------------------------------------------
    {
      id: 'evt-001',
      t_offset_ms: 30000,
      timestamp: '15:38:30',
      title: 'Relay trip attempt — DNP3 FC=5',
      protocol: PROTO.DNP3,
      source_ip: '192.168.1.100',
      source_mac: null,
      source_role: 'Unknown',
      dest_ip: '192.168.1.71',
      dest_role: 'Relay',
      device_name: 'Protection Relay — SEL-351',
      policy_ids: ['P35', 'P38'],
      severity: 'Warning',
      action_status: 'alerted',
      card: {
        observed: '192.168.1.100 sent DNP3 FC=5 (Direct Operate) to SEL-351 relay (192.168.1.71) targeting object group 12 (CROB — Control Relay Output Block)',
        inferred: 'Adversary attempting to trip breaker via relay. FC=5 + object group 12 is the precise sequence used to issue a relay close or trip command.',
        why_it_matters: 'Successful execution causes a breaker state change — potential de-energization of protected bus or false trip. This is a physical consequence attack.',
        recommendation: 'Escalate to Critical. Initiate containment of 192.168.1.100. Preserve full DNP3 session for evidence.',
        confidence: '94%',
      },
      topology_delta: {
        edges_add: [
          {
            id: 'rogue-relay-dnp3',
            source: 'rogue',
            target: 'relay',
            data: { protocol: PROTO.DNP3, state: 'suspicious', label: 'DNP3 FC=5' },
          },
        ],
        nodes_update: {
          rogue: { alertCount: 4, policyHits: 4 },
          rtu: { alertCount: 2, state: STATE.SUSPICIOUS },
          relay: { alertCount: 2, policyHits: 2, state: STATE.SUSPICIOUS },
        },
      },
    },

    // -----------------------------------------------------------------------
    // EVT 2 — T+52s — Cold restart blocked (P36 — Critical)
    // -----------------------------------------------------------------------
    {
      id: 'evt-002',
      t_offset_ms: 52000,
      timestamp: '15:38:52',
      title: 'Cold restart blocked — FC=13 auto-enforcement',
      protocol: PROTO.DNP3,
      source_ip: '192.168.1.100',
      source_mac: null,
      source_role: 'Unknown',
      dest_ip: '192.168.1.70',
      dest_role: 'RTU',
      device_name: 'RTU — Mitsubishi FX5U',
      policy_ids: ['P36'],
      severity: 'Critical',
      action_status: 'blocked',
      card: {
        observed: '192.168.1.100 sent DNP3 FC=13 (Cold Restart) to RTU 192.168.1.70. Policy P36 auto-blocked — enforcement event logged.',
        inferred: 'Adversary escalating from reconnaissance to availability attack. Cold restart forces RTU to reinitialize — real-time data and control loss for restart duration.',
        why_it_matters: 'RTU restart during active operations creates a monitoring blind spot. Combined with the relay trip attempt, this matches a coordinated disruption sequence.',
        recommendation: 'Containment of 192.168.1.100 is now recommended immediately. Block all DNP3 write traffic from this source.',
        confidence: '96%',
      },
      topology_delta: {
        edges_update: {
          'rogue-rtu-dnp3': { data: { protocol: PROTO.DNP3, state: 'critical', label: 'FC=13 BLOCKED' } },
        },
        nodes_update: {
          rogue: { alertCount: 5, policyHits: 5, state: STATE.SUSPICIOUS },
          rtu: { alertCount: 4, policyHits: 3, state: STATE.CRITICAL },
        },
      },
    },

    // -----------------------------------------------------------------------
    // EVT 3 — T+68s — Unauthorized MMS write blocked (P40, P41 — Critical)
    // -----------------------------------------------------------------------
    {
      id: 'evt-003',
      t_offset_ms: 68000,
      timestamp: '15:39:08',
      title: 'Unauthorized MMS write to relay — blocked',
      protocol: PROTO.MMS,
      source_ip: '192.168.1.100',
      source_mac: null,
      source_role: 'Unknown',
      dest_ip: '192.168.1.71',
      dest_role: 'Relay',
      device_name: 'Protection Relay — SEL-351',
      policy_ids: ['P40', 'P41'],
      severity: 'Critical',
      action_status: 'blocked',
      card: {
        observed: '192.168.1.100 sent IEC 61850 MMS write service to SEL-351 relay (192.168.1.71). Source not in SCADA allowlist. Auto-blocked via P40.',
        inferred: 'Adversary pivoted to IEC 61850 after DNP3 path was blocked. Attempting to modify relay protection settings via MMS.',
        why_it_matters: 'MMS writes to a protection relay can alter trip thresholds, time delays, and zone settings — causing relay misoperation under fault conditions.',
        recommendation: 'Rogue device confirmed adversarial. Multiple protocol attempts against protection assets. ISOLATE IMMEDIATELY.',
        confidence: '98%',
      },
      topology_delta: {
        edges_add: [
          {
            id: 'rogue-relay-mms',
            source: 'rogue',
            target: 'relay',
            data: { protocol: PROTO.MMS, state: 'critical', label: 'MMS write BLOCKED' },
          },
        ],
        nodes_update: {
          rogue: { alertCount: 7, policyHits: 7, state: STATE.CRITICAL },
          relay: { alertCount: 4, policyHits: 4, state: STATE.CRITICAL },
        },
      },
    },

    // -----------------------------------------------------------------------
    // EVT 4 — T+84s — GOOSE spoofing (P43 — Warning)
    // -----------------------------------------------------------------------
    {
      id: 'evt-004',
      t_offset_ms: 84000,
      timestamp: '15:39:24',
      title: 'GOOSE from unrecognized relay MAC',
      protocol: PROTO.GOOSE,
      source_ip: null,
      source_mac: 'aa:bb:cc:dd:ee:ff',
      source_role: 'Unknown',
      dest_ip: '192.168.1.71',
      dest_role: 'Relay',
      device_name: 'Protection Relay — SEL-351',
      policy_ids: ['P43'],
      severity: 'Warning',
      action_status: 'alerted',
      card: {
        observed: 'IEC 61850 GOOSE frame from MAC aa:bb:cc:dd:ee:ff — not in authorized relay list. stNum incremented — new protection state published.',
        inferred: 'Unknown device publishing GOOSE protection status. Could be rogue relay, spoofed frame, or compromised device. GOOSE has no Layer 2 authentication.',
        why_it_matters: 'Spoofed GOOSE can trigger cascading relay operations across the protection scheme without any DNP3 or MMS traffic — independent attack vector.',
        recommendation: 'Trace MAC aa:bb:cc:dd:ee:ff to physical port. If port unknown, treat as adversarial GOOSE injection. Isolate at switch level.',
        confidence: '81%',
      },
      topology_delta: {
        edges_add: [
          {
            id: 'unknown-goose-relay',
            source: 'rogue',
            target: 'relay',
            data: { protocol: PROTO.GOOSE, state: 'suspicious', label: 'GOOSE spoof' },
          },
        ],
        nodes_update: {
          relay: { alertCount: 5, policyHits: 5 },
        },
      },
    },

    // -----------------------------------------------------------------------
    // EVT 5 — T+110s — SSH to IED blocked (P46 — Critical)
    // -----------------------------------------------------------------------
    {
      id: 'evt-005',
      t_offset_ms: 110000,
      timestamp: '15:39:50',
      title: 'SSH to IED — blocked',
      protocol: 'ssh',
      source_ip: '192.168.1.100',
      source_mac: null,
      source_role: 'Unknown',
      dest_ip: '192.168.1.72',
      dest_role: 'IED',
      device_name: 'IED — Beckhoff CX5130',
      policy_ids: ['P46'],
      severity: 'Critical',
      action_status: 'blocked',
      card: {
        observed: '192.168.1.100 attempted SSH (port 22) to IED 192.168.1.72. Blocked via P46 — IT protocol on OT segment.',
        inferred: 'Adversary pivoting to direct device access after protocol-level blocks. SSH to an IED bypasses protocol enforcement and allows firmware/config modification.',
        why_it_matters: 'Command-line access to an IED provides persistent foothold independent of SCADA. Could enable configuration extraction or firmware implant.',
        recommendation: 'Containment critical. 192.168.1.100 has now attempted DNP3, MMS, GOOSE, and SSH against RTU, relay, and IED. Full isolation required.',
        confidence: '99%',
      },
      topology_delta: {
        edges_add: [
          {
            id: 'rogue-ied-ssh',
            source: 'rogue',
            target: 'ied',
            data: { protocol: 'ssh', state: 'critical', label: 'SSH BLOCKED' },
          },
        ],
        nodes_update: {
          rogue: { alertCount: 8, policyHits: 8 },
          ied: { alertCount: 2, policyHits: 2, state: STATE.CRITICAL },
        },
      },
    },

    // -----------------------------------------------------------------------
    // EVT 6 — T+135s — AI containment recommendation
    // -----------------------------------------------------------------------
    {
      id: 'evt-006',
      t_offset_ms: 135000,
      timestamp: '15:40:15',
      title: 'Coordinated attack confirmed — containment ready',
      protocol: null,
      source_ip: '192.168.1.100',
      source_mac: null,
      source_role: 'Unknown',
      dest_ip: null,
      dest_role: null,
      device_name: null,
      policy_ids: ['P34', 'P35', 'P36', 'P38', 'P40', 'P41', 'P43', 'P46'],
      severity: 'Critical',
      action_status: 'action_required',
      card: {
        observed: '6 policy violations in 135s from 192.168.1.100 across DNP3, IEC 61850 MMS, GOOSE, and SSH targeting RTU, relay, and IED',
        inferred: 'Coordinated multi-protocol attack against substation protection assets. Progression: recon → relay trip → cold restart → MMS tamper → GOOSE spoof → SSH pivot',
        why_it_matters: 'All critical protection assets have been targeted. Relay protection integrity may be compromised. Next phase likely persistence or cascading disruption.',
        recommendation: 'ISOLATE 192.168.1.100 from all substation segments. Preserve full session evidence. Initiate NERC CIP incident response.',
        confidence: '99%',
      },
      topology_delta: null,
    },
  ],

  // Applied when operator approves the isolation action
  approvalDelta: {
    nodes_update: {
      rogue: { state: STATE.ISOLATED, alertCount: 8, policyHits: 8 },
    },
    edges_update: {
      'rogue-rtu-dnp3': { data: { protocol: PROTO.DNP3, state: 'blocked', label: 'ISOLATED' } },
      'rogue-relay-dnp3': { data: { protocol: PROTO.DNP3, state: 'blocked', label: 'ISOLATED' } },
      'rogue-relay-mms': { data: { protocol: PROTO.MMS, state: 'blocked', label: 'ISOLATED' } },
      'unknown-goose-relay': { data: { protocol: PROTO.GOOSE, state: 'blocked', label: 'TRACED' } },
      'rogue-ied-ssh': { data: { protocol: 'ssh', state: 'blocked', label: 'ISOLATED' } },
    },
  },

  afterAction: {
    duration: '2m 15s',
    start: '15:38:00',
    end: '15:40:15',
    status: 'Contained',
    devices: [
      { ip: '192.168.1.100', role: 'Unknown / Rogue', outcome: 'ISOLATED' },
      { ip: '192.168.1.70',  role: 'RTU — Mitsubishi FX5U', outcome: 'Normal — no persistence detected' },
      { ip: '192.168.1.71',  role: 'Protection Relay — SEL-351', outcome: 'Normal — settings verify required' },
      { ip: '192.168.1.72',  role: 'IED — Beckhoff CX5130', outcome: 'Normal — access denied' },
    ],
    attackSequence: [
      'DNP3 recon polling',
      'Relay trip via Direct Operate FC=5',
      'Cold restart attempt FC=13',
      'IEC 61850 MMS write to relay',
      'GOOSE spoofing from unknown MAC',
      'SSH pivot to IED',
    ],
    policiesTriggered: ['P35', 'P36', 'P37', 'P38', 'P39', 'P40', 'P41', 'P43', 'P46'],
    enforcementActions: {
      auto: [
        'Cold restart blocked (P36)',
        'MMS write blocked (P40)',
        'SSH to IED blocked (P46)',
      ],
      manual: ['192.168.1.100 isolated (operator approved)'],
    },
    compliance: [
      { standard: 'NERC CIP-005', note: 'Electronic Security Perimeter — 3 auto-blocks enforced' },
      { standard: 'NERC CIP-007', note: 'System Security Management — 6 events logged with evidence' },
      { standard: 'NERC CIP-010', note: 'Config Change Management — 1 firmware alert, settings verify required' },
    ],
    unresolved: [
      'GOOSE source MAC aa:bb:cc:dd:ee:ff not yet traced to physical switch port',
    ],
  },
};
